import { create } from 'zustand';
import {
  filterByDateRange,
  filterByLangValue,
  filterByName,
  sortByCost,
  sortByExist,
  sortByLangValue,
  sortByName,
} from '../features/table/helpers';
import { PaginationType } from '../types/pagination';
import { SortType } from '../types/props';
import {
  deleteQr,
  fetchQr,
  fetchQrList,
  FetchQrListRequest,
  postQr,
  putQr,
  QrCodeDynamicType,
  QrCodeType,
  restoreQr,
} from './api/qr-api';
import { useAlertStore } from './use-alert-store';
import { useBranchesStore } from './use-branch-store';

interface Store {
  isLoadingFetch?: boolean;
  isLoading?: boolean;
  qr?: Partial<QrCodeType>;
  postQr: (type?: QrCodeDynamicType) => Promise<void>;
  fetchQr: (id: string, type?: QrCodeDynamicType) => Promise<void>;
  putQr: (type?: QrCodeDynamicType) => Promise<void>;
  fetchQrList: (req: FetchQrListRequest) => Promise<void>;
  deleteQrCode: (id: string, type?: QrCodeDynamicType) => Promise<void>;
  restoreQrCode: (id: string, type?: QrCodeDynamicType) => Promise<void>;
  staticList?: QrCodeType[];
  dynamicList?: QrCodeType[];
  handleResetQr: () => void;
  handleUpdateQr: (data: Partial<QrCodeType>) => void;
  pagination?: PaginationType;

  allQrs?: QrCodeType[];
  fetchAllQrs: () => Promise<void>;
}

export const useQrStore = create<Store>((set, get) => ({
  handleUpdateQr: (data) => {
    set((store) => ({
      ...store,
      qr: {
        ...store.qr,
        ...data,
      },
    }));
  },
  fetchAllQrs: async () => {
    set({ isLoadingFetch: true });
    try {
      const resp = await fetchQrList({
        page: 1,
        page_size: 1000,
        is_by_company: true,
      });
      const { qr } = resp;
      set({ allQrs: qr });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по QR', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  handleResetQr: async () => {
    try {
      set({ qr: undefined });
    } catch (e) {
      console.error(e);
    }
  },
  fetchQr: async (id, type) => {
    set({ isLoadingFetch: true });
    try {
      const resp = await fetchQr(id, type);
      set({ qr: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по QR', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  postQr: async (type) => {
    set({ isLoading: true });
    try {
      const qr = get().qr;
      if (!qr) {
        throw new Error('QR is not defined');
      }
      const resp = await postQr(qr, type);
      useAlertStore.getState().add({ label: 'QR-код успешно добавлен', variant: 'success' });
      set({ qr: { ...qr, id: resp.id } });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Произошла ошибка при создании QR-кода', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  putQr: async (type) => {
    set({ isLoading: true });
    try {
      const qr = get().qr;
      if (!qr) {
        throw new Error('QR is not defined');
      }
      const resp = await putQr({ ...qr, tree_id: qr.tree_id || qr.tree?.id }, type);
      useAlertStore.getState().add({ label: 'Данные QR-кода изменены', variant: 'success' });
      set({ qr: { ...qr, id: resp.id } });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Ошибка при изменении данных QR-кода', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchQrList: async (req) => {
    set({ isLoadingFetch: true });
    try {
      const { qr, ...other } = await fetchQrList(req);

      let list = [...qr];

      if (req.type) {
        if (req.name) {
          list = list.filter((item) => filterByLangValue(item.name, req.name));
        }

        if (req.description) {
          list = list.filter((item) => filterByLangValue(item.description, req.description));
        }

        if (req.id) {
          list = list.filter((item) => filterByName(item.id, req.id));
        }

        if (req.tags) {
          const tags = req.tags.split(',');
          list = list.filter((item) =>
            tags.every((tag) => (item.tags as unknown as number[]).includes(+tag)),
          );
        }

        if (req.services) {
          const services = req.services.split(',');
          list = list.filter((item) =>
            services.every((service) => (item.services as unknown as string[]).includes(service)),
          );
        }

        if (req.places) {
          const places = req.places.split(',');
          list = list.filter((item) => places.includes(String(item.place_id)));
        }

        if (req.created_at) {
          list = filterByDateRange(list, req.created_at, 'created_at');
        }

        if (req.is_attached) {
          list = list.filter((item) =>
            req.is_attached == 'true' ? item.is_attached : !item.is_attached,
          );
        }

        if (req.field == 'is_attached') {
          list = list.sort((a, b) =>
            sortByExist(a.is_attached, b.is_attached, req.dir as SortType),
          );
        }

        if (req.field == 'name') {
          list = list.sort((a, b) => sortByLangValue(a.name, b.name, req.dir as SortType));
        }

        if (req.field == 'description') {
          list = list.sort((a, b) =>
            sortByLangValue(a.description, b.description, req.dir as SortType),
          );
        }

        if (req.field == 'id') {
          list = list.sort((a, b) => sortByName(a.id, b.id, req.dir as SortType));
        }

        if (req.field == 'tags') {
          list = list.sort((a, b) =>
            sortByCost(
              a.tags.map((e) => e.value).length,
              b.tags.map((e) => e.value).length,
              req.dir as SortType,
            ),
          );
        }

        if (req.field == 'services') {
          list = list.sort((a, b) =>
            sortByCost(a.services.length, b.services.length, req.dir as SortType),
          );
        }

        if (req.field == 'created_at') {
          list = list.sort((a, b) => sortByName(a.created_at, b.created_at, req.dir as SortType));
        }

        if (req.field == 'branch') {
          const branches = useBranchesStore.getState().place;

          list = list.map((item) => {
            const branch = branches?.find((branch) => branch.id === item.place_id);
            item.branch = branch;
            return item;
          });

          list = list.sort((a, b) =>
            sortByLangValue(a.branch?.name, b.branch?.name, req.dir as SortType),
          );
        }
        set({ dynamicList: list, pagination: other });
      } else {
        const isInfinite = !!req.infinite;
        const updatedList = isInfinite ? get().staticList?.concat(qr) : qr;

        set({ staticList: updatedList, pagination: other });
      }
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по списку QR', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  deleteQrCode: async (id, type) => {
    set({ isLoading: true });
    try {
      await deleteQr(id, type);
      useAlertStore.getState().add({ label: 'QR-код успешно удален', variant: 'success' });
      const isDynamic = type === 'AUTH_COMMON_DYNAMIC';

      if (isDynamic) {
        const updatedList =
          get().dynamicList?.map((item) =>
            item.id === id ? { ...item, is_deleted: true } : item,
          ) || [];
        set((store) => ({ ...store, dynamicList: updatedList }));
      } else {
        const updatedList =
          get().staticList?.map((item) =>
            item.id === id ? { ...item, is_deleted: true } : item,
          ) || [];
        set((store) => ({ ...store, staticList: updatedList }));
      }
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Произошла ошибка при удалении QR-кода', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restoreQrCode: async (id, type) => {
    set({ isLoading: true });
    try {
      await restoreQr(id, type);
      useAlertStore.getState().add({ label: 'QR-код успешно восстановлен', variant: 'success' });
      const isDynamic = type === 'AUTH_COMMON_DYNAMIC';

      if (isDynamic) {
        const updatedList =
          get().dynamicList?.map((item) =>
            item.id === id ? { ...item, is_deleted: false } : item,
          ) || [];
        set((store) => ({ ...store, dynamicList: updatedList }));
      } else {
        const updatedList =
          get().staticList?.map((item) =>
            item.id === id ? { ...item, is_deleted: false } : item,
          ) || [];
        set((store) => ({ ...store, staticList: updatedList }));
      }
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Произошла ошибка при восстановлении QR-кода', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
